import { useState } from 'react';
import TextField from './fields/TextField';
import NumberField from './fields/NumberField';
import CheckboxField from './fields/CheckboxField';
import RadioField from './fields/RadioField';
import DateField from './fields/DateField';
import SignatureField from './fields/SignatureField';

// Error tooltip component - positioned at top-right corner of the field
const FieldErrorTooltip = ({ message, x, y, width }) => {
  if (!message) return null;
  
  return (
    <div style={{
      position: 'absolute',
      left: `${x + width - 4}px`,
      top: `${y - 28}px`,
      backgroundColor: '#dc2626',
      color: 'white',
      padding: '4px 10px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 700,
      lineHeight: '1.4',
      zIndex: 100,
      boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
      transform: 'translateX(-100%)'
    }}>
      {message}
      {/* Arrow pointing down */}
      <div style={{
        position: 'absolute',
        right: '8px',
        bottom: '-6px',
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid #dc2626'
      }} />
    </div>
  );
};

const PdfFieldOverlay = ({
  fields,
  fieldValues,
  fieldErrors = {},
  onFieldChange,
  fieldConfiguration,
  currentPage,
  scale: rawScale,
  fieldRefs,
  activeErrorField
}) => {
  const scale = rawScale ?? 1.0;
  
  if (!fields || fields.length === 0) {
    return null;
  }

  const orderedFields = [...fields].sort((a, b) => {
    const pageDelta = (a.page || 1) - (b.page || 1);
    if (pageDelta !== 0) return pageDelta;

    const topDelta = (a.y || 0) - (b.y || 0);
    if (Math.abs(topDelta) > 4) return topDelta;

    const leftDelta = (a.x || 0) - (b.x || 0);
    if (Math.abs(leftDelta) > 2) return leftDelta;

    return (a.name || '').localeCompare(b.name || '');
  });

  const renderField = (field) => {
    const value = fieldValues[field.name] || '';
    const config = fieldConfiguration?.fields?.find(f => f.pdfFieldName === field.name);
    const hasError = fieldErrors[field.name];
    const isActiveError = activeErrorField === field.name;
    
    // Scale field positions and sizes according to current PDF scale
    const scaledX = (field.x || 0) * scale;
    const scaledY = (field.y || 0) * scale;
    const scaledWidth = (field.width || 100) * scale;
    const scaledHeight = (field.height || 20) * scale;
    const baseFontSize = config?.fontSize || parseInt(field.fontSize) || 12;
    const scaledFontSize = Math.round(baseFontSize * scale);
    
    const style = {
      left: `${scaledX}px`,
      top: `${scaledY}px`,
      width: `${scaledWidth}px`,
      height: `${scaledHeight}px`,
      fontSize: `${scaledFontSize}px`,
      fontFamily: config?.fontFamily || fieldConfiguration?.globalFontFamily || "'Times New Roman', Times, serif",
      fontWeight: config?.bold ? 'bold' : 'normal',
      fontStyle: config?.italic ? 'italic' : 'normal',
      textDecoration: config?.underline ? 'underline' : 'none',
      color: config?.fontColor || '#000000',
      textAlign: config?.textAlign || 'left',
      border: hasError ? '2px solid #dc3545' : undefined
    };

    switch (field.type) {
      case 'text':
        return (
          <TextField
            key={field.name}
            field={field}
            value={value}
            onChange={onFieldChange}
            style={style}
            required={config?.required}
            maxLength={config?.maxLength}
            ref={el => fieldRefs && (fieldRefs.current[field.name] = el)}
          />
        );
      
      case 'number':
        return (
          <NumberField
            key={field.name}
            field={field}
            value={value}
            onChange={onFieldChange}
            style={style}
            required={config?.required}
            maxLength={config?.maxLength}
            ref={el => fieldRefs && (fieldRefs.current[field.name] = el)}
          />
        );
      
      case 'checkbox':
        // Get markStyle with fallback to 'tick'
        // Check individual field config first, then form-level config
        // Treat 'auto' as 'tick' so checkboxes always render with a visible overlay
        const fieldMarkStyle = config?.markStyle;
        const formMarkStyle = fieldConfiguration?.markStyle;
        const rawMarkStyle = fieldMarkStyle || formMarkStyle || 'tick';
        const markStyle = rawMarkStyle === 'auto' ? 'tick' : rawMarkStyle;
        
        return (
          <CheckboxField
            key={field.name}
            field={field}
            value={value}
            onChange={onFieldChange}
            style={style}
            markStyle={markStyle}
            ref={el => fieldRefs && (fieldRefs.current[field.name] = el)}
          />
        );
      
      case 'radio':
        return (
          <RadioField
            key={field.name}
            field={field}
            groupName={field.groupName || field.name}
            value={field.radioValue || field.name}
            currentValue={fieldValues[field.groupName || field.name]}
            onChange={onFieldChange}
            style={style}
            radioStyle={fieldConfiguration?.radioStyle || 'dot'}
            showBorder={fieldConfiguration?.showRadioBorder !== false}
            ref={el => fieldRefs && (fieldRefs.current[field.name] = el)}
          />
        );
      
      case 'date':
        return (
          <DateField
            key={field.name}
            field={field}
            value={value}
            onChange={onFieldChange}
            style={style}
            dateGroup={config?.metadata?.dateGroup}
            ref={el => fieldRefs && (fieldRefs.current[field.name] = el)}
          />
        );
      
      case 'signature':
        return (
          <SignatureField
            key={field.name}
            field={field}
            value={value}
            onSign={onFieldChange}
            style={style}
            ref={el => fieldRefs && (fieldRefs.current[field.name] = el)}
          />
        );
      
      default:
        return null;
    }
  };

  // Check if a field's visibility condition is met
  const isConditionallyVisible = (field) => {
    if (!field.visibilityDependsOn) return true;
    
    const controllingValue = fieldValues[field.visibilityDependsOn];
    const expectedValue = field.visibilityDependsOnValue;
    
    // Checkbox condition: compare against boolean-like values
    if (expectedValue === 'true') {
      return controllingValue === true || controllingValue === 'true' || controllingValue === 'Yes';
    }
    if (expectedValue === 'false') {
      return !controllingValue || controllingValue === false || controllingValue === 'false' || controllingValue === 'No';
    }
    
    // Radio condition: exact match on the radio option pdfFieldName
    return controllingValue === expectedValue;
  };

  const renderFieldWithTooltip = (field) => {
    // Check conditional visibility — hide field if condition not met
    if (!isConditionallyVisible(field)) {
      return null;
    }

    const hasError = fieldErrors[field.name];
    const scaledX = (field.x || 0) * scale;
    const scaledY = (field.y || 0) * scale;
    const scaledWidth = (field.width || 100) * scale;

    return (
      <div key={`wrapper-${field.name}`}>
        {renderField(field)}
        {hasError && (
          <FieldErrorTooltip
            message={hasError}
            x={scaledX}
            y={scaledY}
            width={scaledWidth}
          />
        )}
      </div>
    );
  };

  return (
    <div className="pdf-field-overlay" style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none'
    }}>
      <div style={{ position: 'relative', pointerEvents: 'auto' }}>
        {orderedFields.map(field => renderFieldWithTooltip(field))}
      </div>
    </div>
  );
};

export default PdfFieldOverlay;
